import React, { useEffect, useState } from 'react';
import Layout from '../../../components/web/Layout';
import { Helmet } from 'react-helmet';
import { X, Calendar, Clock, FileText } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { getFileUrl } from '../../../utils/helper';

function Notification() {
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNotification = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}notification`,
          {
            headers: { Accept: 'application/json' },
          }
        );

        setNotifications(response.data?.notifications || []);
      } catch (error) {
        toast.error('Failed to load notifications!');
      } finally {
        setLoading(false);
      }
    };

    fetchNotification();
  }, []);

  const openModal = (notification) => {
    setSelectedNotification(notification);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedNotification(null);
    document.body.style.overflow = 'unset';
  };

  const isPdf = (fileName) => fileName?.toLowerCase().endsWith('.pdf');

  if (loading) return <p className="text-center mt-10">Loading...</p>;

  return (
    <Layout>
      <Helmet>
        <title>Notifications</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 mb-3">
              Notifications
            </h1>
            <p className="text-gray-600 text-lg">
              Stay updated with the latest announcements
            </p>
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {notifications.map((notification) => {
              const fileUrl = getFileUrl(notification.image, 'banners');
              const pdf = isPdf(notification.image);

              return (
                <div
                  key={notification.id}
                  onClick={() => openModal(notification)}
                  className="group bg-white rounded-2xl shadow-md overflow-hidden cursor-pointer transform transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 border border-gray-100"
                >
                  {/* Image or PDF Preview */}
                  <div className="relative h-56 overflow-hidden bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                    {pdf ? (
                      <div className="flex flex-col items-center justify-center text-center p-6">
                        <FileText className="w-12 h-12 text-indigo-600 mb-2" />
                        <p className="text-sm font-semibold text-gray-700">
                          PDF Document
                        </p>
                      </div>
                    ) : (
                      <img
                        src={fileUrl}
                        alt={notification.heading}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 group-hover:rotate-2"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 shadow-lg">
                      <span className="text-xs font-semibold text-indigo-600">New</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-gray-800 mb-3 line-clamp-2 group-hover:text-indigo-600 transition-colors duration-300">
                      {notification.heading}
                    </h3>
                    <div className="flex items-center text-sm text-gray-500 bg-gray-50 rounded-lg p-2">
                      <Clock className="w-4 h-4 mr-2 text-indigo-500" />
                      <span className="font-medium">
                        {new Date(notification.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div className="h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                </div>
              );
            })}
          </div>

          {/* Modal */}
          {selectedNotification && (
            <div
              className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn"
              onClick={closeModal}
            >
              <div
                className="bg-white rounded-3xl max-w-4xl w-full max-h-[95vh] overflow-y-auto shadow-2xl transform transition-all duration-500 animate-scaleIn"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close Button */}
                <button
                  onClick={closeModal}
                  className="fixed top-6 right-6 bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-xl hover:bg-red-500 hover:text-white transition-all duration-300 z-10 group"
                >
                  <X className="w-6 h-6 text-gray-700 group-hover:text-white group-hover:rotate-90 transition-all duration-300" />
                </button>

                {/* Image or PDF View */}
                <div className="relative overflow-hidden rounded-t-3xl">
                  {isPdf(selectedNotification.image) ? (
                    <iframe
                      src={getFileUrl(selectedNotification.image, 'banners')}
                      title="PDF Preview"
                      className="w-full h-[600px] border-none"
                    />
                  ) : (
                    <img
                      src={getFileUrl(selectedNotification.image, 'banners')}
                      alt={selectedNotification.heading}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>

                {/* Modal Content */}
                <div className="p-6 sm:p-10">
                  <h2 className="text-sm sm:text-xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 mb-6 leading-tight">
                    {selectedNotification.heading}
                  </h2>

                  <div className="inline-flex items-center bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl px-5 py-3 mb-6">
                    <Calendar className="w-5 h-5 mr-3 text-indigo-600" />
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Posted on</p>
                      <p className="text-sm font-bold text-gray-800">
                        {new Date(selectedNotification.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-gray-50 to-indigo-50/30 rounded-2xl p-6 border border-gray-100">
                    <p className="text-gray-700 leading-relaxed text-lg mb-4">
                      {selectedNotification.description ||
                        'This notification contains important information.'}
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 mt-8">
                    <button
                      onClick={closeModal}
                      className="flex-1 bg-gray-100 text-gray-700 font-bold py-4 px-6 rounded-xl hover:bg-gray-200 transform hover:scale-105 transition-all duration-300"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Animations */}
        <style jsx>{`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes scaleIn {
            from {
              opacity: 0;
              transform: scale(0.9);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }

          .animate-fadeIn {
            animation: fadeIn 0.3s ease-out;
          }

          .animate-scaleIn {
            animation: scaleIn 0.4s ease-out;
          }

          .line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        `}</style>
      </div>
    </Layout>
  );
}

export default Notification;
